import { useViewMapQueryParams } from 'hooks/map/useViewMapQueryParams';
import { setCookie } from 'libs/utils/logic/operations/storageOperations';
import qs from 'query-string';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { createSearchParams } from 'react-router-dom';
import { CtxView } from 'session/ctxView';
import { LOCAL_STORAGE_LANGUAGE } from 'static/config/coreConfig';
import { FlagCs } from 'static/flags/flag_cs';
import { FlagEn } from 'static/flags/flag_en';
import { FlagNb } from 'static/flags/flag_nb';
import { FlagSk } from 'static/flags/flag_sk';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const MapV2LanguageSwitch = (props: Props) => {
    const ctxView = useContext(CtxView);
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const params = qs.parse(location.search) as { lang?: string };
    const [currentLanguage, setCurrentLanguage] = useState(useViewMapQueryParams()?.language);

    function updateLanguage(lang: string) {
        if (currentLanguage === lang) {
            return;
        }
        const newParams = createSearchParams({ ...params, lang });
        setCookie(LOCAL_STORAGE_LANGUAGE, lang);
        setCurrentLanguage(lang);
        setIsOpen(false);
        window.location.search = newParams.toString();
    }

    useEffect(() => {
        function clickHandler() {
            setIsOpen(false);
        }

        if (!isOpen) {
            return;
        }

        document.addEventListener('click', clickHandler);

        return () => {
            document.removeEventListener('click', clickHandler);
        };
    }, [isOpen]);

    const flags = {
        en: <FlagEn height={18} />,
        nb: <FlagNb height={18} />,
        cs: <FlagCs height={18} />,
        sk: <FlagSk height={18} />
    };

    const currentFlag = flags[currentLanguage];
    const otherFlags = Object.entries(flags)
        .filter(([key]) => key !== currentLanguage)
        .filter(([key]) => ctxView.availableLanguages.includes(key));

    if (!otherFlags.length) {
        return;
    }

    return (
        <S_MapV2LanguageSwitch className={props.className} $isOpen={isOpen}>
            <S_CurrentFlag
                onClick={(e) => {
                    setIsOpen((curr) => !curr);
                    e.stopPropagation();
                }}
            >
                {currentFlag}
                {/* <S_Arrow icon={['fas', 'caret-up']} /> */}
            </S_CurrentFlag>
            <S_FlagOptions
                $isOpen={isOpen}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {otherFlags.map(([key, flag]) => {
                    return (
                        <S_Option
                            key={key}
                            onClick={() => {
                                updateLanguage(key);
                            }}
                        >
                            {flag}
                        </S_Option>
                    );
                })}
            </S_FlagOptions>
        </S_MapV2LanguageSwitch>
    );
};

const S_MapV2LanguageSwitch = styled.div<{ $isOpen: boolean }>`
    position: relative;
    background: #fff;
    border-radius: ${(props) => (props.$isOpen ? '0 10px 10px 0' : '10px')};
    align-items: center;
    justify-content: center;
    display: flex;
    flex-direction: column;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
`;

const S_CurrentFlag = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    height: 45px;
    padding: 8px 10px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    z-index: 5;
`;

const S_FlagOptions = styled.div<{ $isOpen: boolean }>`
    display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
    position: absolute;
    flex-direction: column;
    gap: 0;
    align-items: stretch;
    background: #fff;
    border-radius: 10px;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
    bottom: 0;
    flex-direction: row;
    right: 0;
    padding-right: 45px;
    left: unset;
`;

const S_Option = styled.div`
    cursor: pointer;
    padding: 8px 10px;
    width: 45px;
    height: 45px;
    display: flex;
    align-items: center;
    &:hover {
        background: #f5f5f5;
        border-radius: 10px;
    }
`;
